import { Control, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui-kit/accordion';
import { FormControl, FormField, FormItem, FormLabel } from '@/components/ui-kit/form';
import { Textarea } from '@/components/ui-kit/textarea';
import { Button } from '@/components/ui-kit/button';
import { Switch } from '@/components/ui-kit/switch';
import { Brain } from 'lucide-react';
import { AgentBehaviorSettingsForm } from '../utils';
import { PresetQuestionsList } from './preset-questions-list';

import {
  useGetAgentById,
  useUpdateAgentAIConfigurations,
  useUpdateWidget,
} from '@/modules/gpt-chats/hooks/use-agents';
import { showErrorToast, showSuccessToast } from '@/hooks/use-toast';
import { ErrorTransformer } from '@/modules/gpt-chats/utils/error-transform';
import { IUpdateAgentAIConfigurationPayload } from '@/modules/gpt-chats/types/agent.service.type';
import { GreetingAIModal } from './greeting-ai-modal';

interface WelcomeGuideSectionProps {
  control: Control<AgentBehaviorSettingsForm>;
  form: UseFormReturn<AgentBehaviorSettingsForm>;
  isMobile: boolean;
  agentId: string;
  onSubmit: (values: AgentBehaviorSettingsForm) => Promise<void>;
}

export const WelcomeGuideSection = ({
  control,
  form,
  agentId,
  onSubmit,
}: WelcomeGuideSectionProps) => {
  const { t } = useTranslation();
  const { data } = useGetAgentById(agentId);
  const { mutateAsync: updateWidget } = useUpdateWidget();
  const { mutateAsync } = useUpdateAgentAIConfigurations();

  const [isGreetingAIModalOpen, setIsGreetingAIModalOpen] = useState(false);
  const [, setShowSmartSuggestions] = useState(true);
  const [showPresetQuestions, setShowPresetQuestions] = useState(true);
  const [greetingValue, setGreetingValue] = useState('');
  const [presetQuestionsLocal, setPresetQuestionsLocal] = useState<string[]>(
    () => data?.agent?.widget?.predefined_questions || []
  );

  const watchedGreeting = form.watch('widget.greeting');

  useEffect(() => {
    setGreetingValue(watchedGreeting || '');
  }, [watchedGreeting]);

  useEffect(() => {
    if (data?.agent?.widget?.predefined_questions) {
      setPresetQuestionsLocal(data.agent.widget.predefined_questions);
    }
  }, [data?.agent?.widget?.predefined_questions]);

  useEffect(() => {
    if (data?.agent?.widget) {
      setShowSmartSuggestions(!!data.agent.memory_config.enable_questions_suggestions);
      setShowPresetQuestions(!!data.agent.widget.enable_predefined_questions);
    }
  }, [data?.agent?.widget, data?.agent?.memory_config]);

  const commitPresetQuestions = async (questions: string[]) => {
    if (!data?.agent) return showErrorToast({ errors: t('SOMETHING_WENT_WRONG') });

    const payloadQuestions = questions.map((q) => q.trim()).filter(Boolean);

    try {
      const widgetRes = await updateWidget({
        agent_id: agentId,
        regen_widget_id: false,
        widget: {
          ...data.agent.widget,
          predefined_questions: payloadQuestions,
        },
      });
      if (!widgetRes.is_success)
        return showErrorToast({ errors: ErrorTransformer(widgetRes.detail) });
    } catch (err) {
      return showErrorToast({ errors: ErrorTransformer(err) });
    }

    try {
      const updatePayload = {
        agent_id: agentId,
        name: data.agent.name,
        description: data.agent.description,
        rag_config: {
          ...data.agent.rag_config,
          embedding_model_provider: 'azure',
        },
        llm_config: {
          ...data.agent.llm_config,
          model_id: (data.agent.llm_config?.model_id as string) || '',
          base_prompt: (data.agent.llm_config?.base_prompt as string) || '',
        },
        memory_config: data.agent.memory_config,
        guardrail: data.agent.guardrail,
        widget: {
          ...data.agent.widget,
          predefined_questions: payloadQuestions,
        },
      } as unknown as IUpdateAgentAIConfigurationPayload;

      const res = await mutateAsync(updatePayload);
      if (!res.is_success) return showErrorToast({ errors: ErrorTransformer(res.detail) });

      showSuccessToast({ description: t('PRESET_QUESTIONS_UPDATED') });
    } catch (err) {
      return showErrorToast({ errors: ErrorTransformer(err) });
    }
  };

  const commitWidgetBoolean = async (widgetKey: string, value: boolean) => {
    if (!data?.agent) return showErrorToast({ errors: t('SOMETHING_WENT_WRONG') });

    try {
      const widgetRes = await updateWidget({
        agent_id: agentId,
        regen_widget_id: false,
        widget: {
          ...data.agent.widget,
          [widgetKey]: value,
        },
      });
      if (!widgetRes.is_success)
        return showErrorToast({ errors: ErrorTransformer(widgetRes.detail) });
    } catch (err) {
      return showErrorToast({ errors: ErrorTransformer(err) });
    }

    try {
      const updatePayload = {
        agent_id: agentId,
        name: data.agent.name,
        description: data.agent.description,
        rag_config: {
          ...data.agent.rag_config,
          embedding_model_provider: 'azure',
        },
        llm_config: {
          ...data.agent.llm_config,
          model_id: (data.agent.llm_config?.model_id as string) || '',
          base_prompt: (data.agent.llm_config?.base_prompt as string) || '',
        },
        memory_config: data.agent.memory_config,
        guardrail: data.agent.guardrail,
        widget: {
          ...data.agent.widget,
          [widgetKey]: value,
        },
      } as unknown as IUpdateAgentAIConfigurationPayload;

      const res = await mutateAsync(updatePayload);
      if (!res.is_success) return showErrorToast({ errors: ErrorTransformer(res.detail) });

      showSuccessToast({ description: t('AGENT_UPDATED_SUCCESSFULLY') });
    } catch (err) {
      return showErrorToast({ errors: ErrorTransformer(err) });
    }
  };
  return (
    <AccordionItem value="welcome">
      <AccordionTrigger className="px-5 text-lg hover:no-underline">
        {t('WELCOME_GUIDE')}
      </AccordionTrigger>
      <AccordionContent className="grid gap-6 px-5">
        <div>
          <div className="mb-3 flex items-center justify-between">
            <FormLabel className="text-sm font-medium">{t('GREETING')}</FormLabel>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="flex items-center gap-2 rounded-[200px] border-primary text-primary hover:bg-primary/10"
              onClick={() => setIsGreetingAIModalOpen(true)}
            >
              <Brain className="h-4 w-4" />
              {t('AI')}
            </Button>
          </div>
          <FormField
            control={control}
            name="widget.greeting"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Textarea
                    value={greetingValue}
                    onChange={(e) => setGreetingValue(e.target.value)}
                    onBlur={() => {
                      const currentValue = form.getValues('widget.greeting');
                      if (currentValue !== greetingValue) {
                        field.onChange(greetingValue);
                        onSubmit(form.getValues());
                      }
                    }}
                    placeholder={t('GREETING_PLACEHOLDER')}
                    rows={3}
                    className="resize-none border bg-card focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <div className={`w-full space-y-6 xl:w-1/2`}>
          <div className="flex items-center justify-between">
            <FormLabel className="text-sm font-medium">{t('SMART_SUGGESTIONS')}</FormLabel>
            <FormField
              control={control}
              name="memory_config.enable_questions_suggestions"
              render={({ field }) => (
                <Switch checked={!!field.value} onCheckedChange={field.onChange} />
              )}
            />
          </div>

          <div className="flex items-center justify-between">
            <FormLabel className="text-sm font-medium">{t('PRESET_QUESTIONS')}</FormLabel>
            <Switch
              checked={showPresetQuestions}
              onCheckedChange={async (val) => {
                setShowPresetQuestions(val);
                await commitWidgetBoolean('enable_predefined_questions', !!val);
              }}
              // size="sm"
            />
          </div>
        </div>

        {showPresetQuestions && (
          <PresetQuestionsList
            questions={presetQuestionsLocal}
            onChange={setPresetQuestionsLocal}
            onSave={commitPresetQuestions}
          />
        )}
      </AccordionContent>

      <GreetingAIModal
        isOpen={isGreetingAIModalOpen}
        onClose={() => setIsGreetingAIModalOpen(false)}
        onUse={(greeting) => form.setValue('widget.greeting', greeting)}
        agentName={data?.agent?.name}
        greetingValue={greetingValue}
      />
    </AccordionItem>
  );
};
